// Ο διακόπτης των προκαταβολών ταμείου ανά κατάστημα, ΜΙΑ φορά (κοινή μνήμη για όλες τις οθόνες:
// κρατήσεις, «Ταμείο», ρυθμίσεις). Αποτυχία/παλιό backend = κλειστό — η οθόνη δεν δείχνει τίποτα.
// Η αποθήκευση από τις ρυθμίσεις καθαρίζει τη μνήμη (invalidateDepositsConfig).

import { useCallback, useEffect, useState } from 'react';
import { depositsApi } from './api';
import type { DepositsConfig, PosTerminalLite } from './types';

/** Ο διακόπτης ξαναδιαβάζεται το πολύ κάθε 5′ (χωρίς F5 στα ανοιχτά ταμεία). */
const TTL_MS = 5 * 60 * 1000;
const cache = new Map<number, { at: number; promise: Promise<DepositsConfig | null> }>();

export function loadDepositsConfig(storeId: number, force = false): Promise<DepositsConfig | null> {
  const hit = cache.get(storeId);
  if (!force && hit && Date.now() - hit.at < TTL_MS) return hit.promise;
  const promise = depositsApi.config(storeId).then(
    c => (c && typeof c === 'object' ? { ...c, enabled: c.enabled === true, ready: c.ready !== false, ecr: c.ecr === true } : null),
    () => {
      // Αποτυχία: όχι στη μνήμη, ξαναδοκιμάζει στο επόμενο άνοιγμα.
      if (cache.get(storeId)?.promise === promise) cache.delete(storeId);
      return null;
    },
  );
  cache.set(storeId, { at: Date.now(), promise });
  return promise;
}

export function invalidateDepositsConfig(storeId?: number) {
  if (storeId == null) cache.clear();
  else cache.delete(storeId);
}

/** Για τα tests. */
export const resetDepositsConfigCache = () => cache.clear();

export function useDepositsConfig(storeId: number | null | undefined) {
  const [state, setState] = useState<{ id: number | null; config: DepositsConfig | null; loaded: boolean }>(
    { id: null, config: null, loaded: false },
  );
  const reload = useCallback((force = true) => {
    if (!storeId) return Promise.resolve(null);
    return loadDepositsConfig(storeId, force).then(c => { setState({ id: storeId, config: c, loaded: true }); return c; });
  }, [storeId]);
  useEffect(() => {
    if (!storeId) return undefined;
    let alive = true;
    loadDepositsConfig(storeId).then(c => { if (alive) setState({ id: storeId, config: c, loaded: true }); });
    return () => { alive = false; };
  }, [storeId]);
  const current = state.id === storeId ? state : { config: null, loaded: false };
  return {
    config: current.config,
    loaded: current.loaded,
    /** Η πύλη του UI: μόνο με ανοιχτό διακόπτη. */
    enabled: current.config?.enabled === true,
    reload,
  };
}

/** Τα ενεργά τερματικά — μόνο όταν χρειάζονται (κάρτα/IRIS). */
export function useActiveTerminals(storeId: number, needed: boolean) {
  const [terminals, setTerminals] = useState<PosTerminalLite[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!needed || terminals) return undefined;
    let alive = true;
    depositsApi.terminals(storeId)
      .then(t => { if (alive) setTerminals(t); })
      .catch(() => { if (alive) { setTerminals([]); setError(true); } });
    return () => { alive = false; };
  }, [storeId, needed, terminals]);
  return { terminals, error };
}

/** Το τερματικό που διάλεξε ο ταμίας στα τραπέζια (ίδιο κλειδί με το DineInOperations). */
export function savedTerminalId(employeeId: number | null | undefined, storeId: number): number | null {
  try {
    const v = localStorage.getItem(`pos_terminal_${employeeId}_${storeId}`);
    return v ? Number.parseInt(v, 10) || null : null;
  } catch {
    return null;
  }
}

/** Προεπιλογή: το προτιμώμενο (αν είναι ενεργό), αλλιώς το μοναδικό. Με πολλά και χωρίς προτίμηση: καμία. */
export function pickTerminal(terminals: PosTerminalLite[] | null, preferred: Array<number | null | undefined>): number | null {
  if (!terminals || terminals.length === 0) return null;
  for (const id of preferred) if (id != null && terminals.some(t => t.id === id)) return id;
  return terminals.length === 1 ? terminals[0].id : null;
}
